/* eslint-disable sonarjs/cognitive-complexity */
'use client';

import { shadow } from '@ahamove/tokens';
import { cn, formatCurrency } from '@ahm/common-helpers';
import {
  Box,
  Button,
  Card,
  Group,
  Image,
  Stack,
  Text,
  rem,
} from '@mantine/core';

import { useMediaQuery } from '@mantine/hooks';
import { useTranslations } from 'next-intl';
import { useTransition } from 'react';
import InfoTooltip from '@/components/info-tooltip';
import { ImageZoom } from '@/components/ui/kibo-ui/image-zoom';
import { useLocaleSuffix, useRouter } from '@/navigation';

type ModalReimbursementFeeProps = {
  onClose: () => void;
  recordDetail: any;
  reimbursement: {
    total_fee: number;
    requests: {
      id: number;
      description: string;
      amount: number;
      image_urls: string[];
    }[];
  };
};

export default function ReimbursementFeeModal({
  reimbursement,
  recordDetail,
}: ModalReimbursementFeeProps) {
  const localeSuffix = useLocaleSuffix();
  const tActions = useTranslations('common.actions');

  const { requests } = reimbursement || {};
  const isMobile = useMediaQuery('(max-width: 768px)');
  const router = useRouter();
  const [isPendingTransition, startTransition] = useTransition();

  function handleCheckoutReimbursement() {
    startTransition(() => {
      router.push(`/checkout/reimbursement/${recordDetail?._id as string}`);
    });
  }

  if (!requests?.length) return null;

  return (
    <>
      <Card p={isMobile ? 12 : 24}>
        <Text size={isMobile ? 'xs' : 'sm'} fw={500} mb={8}>
          Tài xế của bạn sẽ nhận được tổng phí đã phát sinh trong quá trình giao
          hàng sau khi bạn thanh toán thành công
        </Text>
        <Stack p={0} gap={isMobile ? 'sm' : 'md'} mt={isMobile ? 8 : 16}>
          {requests.map((item) => (
            <Card
              key={item.id}
              withBorder
              radius={8}
              mb={0}
              style={{ boxShadow: shadow['8'] }}
            >
              <Stack gap={4}>
                <Group wrap="nowrap" justify="space-between" align="center">
                  <Group wrap="nowrap" gap={4}>
                    <Text lineClamp={2} size={isMobile ? 'xs' : 'sm'}>
                      {item[`name_${localeSuffix}`]}
                    </Text>
                    <InfoTooltip label={item[`description_${localeSuffix}`]} />
                  </Group>

                  <Text flex="none" fw={700} size={isMobile ? 'sm' : 'md'}>
                    {formatCurrency(item.amount)}
                  </Text>
                </Group>
                {/* <Text size="xs" c="neutral.6" mb={4}>
                  Hình ảnh xác thực
                </Text> */}
                <Group gap={8} wrap="wrap">
                  {item.image_urls.map((url, imgIdx) => (
                    <Box
                      key={imgIdx}
                      w={rem(50)}
                      h={rem(50)}
                      style={{
                        borderRadius: 8,
                        overflow: 'hidden',
                        boxShadow: shadow['8'],
                      }}
                    >
                      <ImageZoom
                        backdropClassName={cn(
                          '[&_[data-rmiz-modal-overlay="visible"]]:bg-black/80'
                        )}
                      >
                        <Image
                          src={url}
                          alt="Reimbursement"
                          w={rem(50)}
                          h={rem(50)}
                          fit="cover"
                          style={{ cursor: 'pointer' }}
                        />
                      </ImageZoom>
                    </Box>
                  ))}
                </Group>
              </Stack>
            </Card>
          ))}
        </Stack>
      </Card>

      <Group
        justify="flex-end"
        mt="auto"
        gap={8}
        p="md"
        pos={{ base: 'sticky', sm: 'sticky' }}
        bottom={0}
        left={0}
        w="100%"
        bg="white"
        styles={{
          root: {
            zIndex: 100,
            borderTop: `1px solid var(--mantine-color-neutral-1)`,
          },
        }}
      >
        {isMobile ? (
          <>
            <Group w="100%" gap={rem(8)} justify="space-between" align="center">
              <Text c="neutral.6" size="sm">
                Tổng phí phát sinh
              </Text>
              <Text size="md" fw={600} c="primary.5">
                {formatCurrency(reimbursement.total_fee || 0)}
              </Text>
            </Group>
            <Button fullWidth size="md" mih={rem(48)} type="submit">
              {tActions('confirm')}
            </Button>
          </>
        ) : (
          <Group justify="flex-end" w="100%" align="center" p={0}>
            <Group w="100%" justify="space-between" gap={12}>
              <Text fw={600} c="neutral.6" size="sm">
                Tổng phí phát sinh
              </Text>
              <Text fw={700} c="primary.6" size="xl">
                {formatCurrency(reimbursement.total_fee || 0)}
              </Text>
            </Group>

            <Button
              fullWidth
              size="md"
              mih={rem(48)}
              onClick={handleCheckoutReimbursement}
              style={{ whiteSpace: 'nowrap' }}
              loading={isPendingTransition}
            >
              {tActions('confirm')}
            </Button>
          </Group>
        )}
      </Group>
    </>
  );
}
